import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import ConstructionContextSelector from '../../components/ConstructionContextSelector';
import LogQuantityEvidence from '../../components/LogQuantityEvidence';
import LinkActivityModal from '../../components/LinkActivityModal';
import { Play, Loader2, CheckCircle2, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OBJECT_TYPES = [
  { value: 'room', label: 'Room' },
  { value: 'facade', label: 'Facade' },
  { value: 'floor', label: 'Floor Slab' },
  { value: 'slab', label: 'Slab' },
  { value: 'staircase', label: 'Staircase' },
  { value: 'plot', label: 'Plot Boundary' },
  { value: 'custom', label: 'Custom Object' }
];

const Measure3D = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [objectType, setObjectType] = useState('room');
  const [selectedUploadIds, setSelectedUploadIds] = useState([]);
  const [notes, setNotes] = useState('');
  const [running, setRunning] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [constructionCtx, setConstructionCtx] = useState({});
  const [showLogEvidence, setShowLogEvidence] = useState(false);
  const [showLinkActivity, setShowLinkActivity] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchUploads();
    fetchJobs();
  }, [projectId]);

  useEffect(() => {
    let pollInterval;
    if (currentJob && (currentJob.status === 'pending' || currentJob.status === 'processing')) {
      pollInterval = setInterval(() => pollJobStatus(currentJob.id), 2000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentJob]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to fetch project');
    }
  };

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/uploads`);
      setUploads(response.data.filter(u => u.mimeType.startsWith('image/')));
    } catch (error) {
      console.error('Failed to fetch uploads');
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/analysis-jobs?type=CIVIL_SURFACE_3D`);
      const roomJobs = await axios.get(`${API}/projects/${projectId}/analysis-jobs?type=ROOM_3D`);
      setJobs([...response.data, ...roomJobs.data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (error) {
      console.error('Failed to fetch jobs');
    }
  };

  const pollJobStatus = async (jobId) => {
    try {
      const response = await axios.get(`${API}/analysis-jobs/${jobId}`);
      setCurrentJob(response.data);
      if (response.data.status === 'completed') {
        setRunning(false);
        toast.success('Analysis completed!');
        fetchJobs();
      } else if (response.data.status === 'failed') {
        setRunning(false);
        toast.error('Analysis failed');
      }
    } catch (error) {
      console.error('Failed to poll job status');
    }
  };

  const runAnalysis = async () => {
    if (selectedUploadIds.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setRunning(true);
    try {
      const payload = { objectType, uploadIds: selectedUploadIds, notes, constructionContext: Object.keys(constructionCtx || {}).length ? constructionCtx : null };
      const response = await axios.post(`${API}/projects/${projectId}/measure/3d/run`, payload);
      setCurrentJob(response.data);
      toast.success('Analysis started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start analysis');
      setRunning(false);
    }
  };

  const toggleUpload = (uploadId) => {
    if (selectedUploadIds.includes(uploadId)) {
      setSelectedUploadIds(selectedUploadIds.filter(id => id !== uploadId));
    } else {
      setSelectedUploadIds([...selectedUploadIds, uploadId]);
    }
  };

  const result = currentJob?.result;
  const totals = result?.totals;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">3D Surface Estimation</h1>
          <p className="text-slate-600 mt-1">{project?.name}</p>
        </div>

        <ConstructionContextSelector projectId={projectId} value={constructionCtx} onChange={setConstructionCtx} />

        {/* Object Type Selection */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">1. Select Object Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {OBJECT_TYPES.map(type => (
              <button key={type.value} onClick={() => setObjectType(type.value)} className={`p-3 border-2 rounded-lg transition-all ${objectType === type.value ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid={`object-type-${type.value}`}>
                <Package className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                <p className="text-sm font-medium">{type.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Image Selection */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">2. Select Images (Multiple)</h2>
          <p className="text-sm text-slate-600 mb-4">Select multiple images of the same object from different angles for better accuracy</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {uploads.map(upload => (
              <div key={upload.id} onClick={() => toggleUpload(upload.id)} className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedUploadIds.includes(upload.id) ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid={`upload-item-${upload.id}`}>
                {selectedUploadIds.includes(upload.id) && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="w-full h-20 bg-slate-100 rounded flex items-center justify-center mb-2">
                  <Package className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-xs text-slate-600 truncate">{upload.fileName}</p>
              </div>
            ))}
          </div>
          {selectedUploadIds.length > 0 && (
            <p className="mt-3 text-sm text-blue-600">{selectedUploadIds.length} image(s) selected</p>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">3. Additional Notes (Optional)</h2>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any specific measurements or context..." className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg" rows={3} data-testid="notes-input" />
        </div>

        {/* Run Analysis */}
        <button onClick={runAnalysis} disabled={running || selectedUploadIds.length === 0} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-md" data-testid="run-analysis-btn">
          {running ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Analyzing...</span></> : <><Play className="w-5 h-5" /><span>Run 3D Analysis</span></>}
        </button>

        {/* Results */}
        {currentJob && currentJob.status === 'completed' && totals && (
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Analysis Results</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowLogEvidence(true)} className="px-3 py-1 text-sm rounded-full bg-blue-600 text-white hover:bg-blue-700" data-testid="log-evidence-btn">Log as BOQ Evidence</button>
                <button onClick={() => setShowLinkActivity(true)} className="px-3 py-1 text-sm rounded-full bg-sky-700 text-white hover:bg-sky-800" data-testid="link-activity-btn">Link to Activity</button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-600 mb-1">Net Surface Area</p>
                <p className="text-xl font-bold text-slate-900">{totals.netSurfaceSqM?.toFixed(2)} m²</p>
                <p className="text-xs text-slate-500">{totals.netSurfaceSqFt?.toFixed(2)} ft²</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-slate-600 mb-1">Perimeter</p>
                <p className="text-xl font-bold text-slate-900">{totals.perimeterM?.toFixed(2)} m</p>
                <p className="text-xs text-slate-500">{totals.perimeterFt?.toFixed(2)} ft</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-sm text-slate-600 mb-1">Confidence</p>
                <p className="text-xl font-bold text-slate-900">{((totals.confidence || 0) * 100).toFixed(0)}%</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <p className="text-sm text-slate-600 mb-1">Object Type</p>
                <p className="text-xl font-bold text-slate-900">{result.objectType}</p>
              </div>
            </div>

            {/* Material Estimations */}
            {result.estimations && (
              <div className="mb-6">
                <h3 className="text-md font-semibold text-slate-900 mb-3">Material Estimations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">Paint (2 coats)</p>
                    <p className="text-2xl font-bold text-slate-900">{result.estimations.paintLiters_2coats?.toFixed(1)} L</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">Tiles (2x2 ft)</p>
                    <p className="text-2xl font-bold text-slate-900">{result.estimations.tileCount_2x2ft} tiles</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">Plaster/Cement</p>
                    <p className="text-2xl font-bold text-slate-900">{result.estimations.plasterCementKg?.toFixed(1)} kg</p>
                  </div>
                </div>
              </div>
            )}

            {/* Surfaces */}
            {result.surfaces && result.surfaces.length > 0 && (
              <div>
                <h3 className="text-md font-semibold text-slate-900 mb-3">Surface Breakdown</h3>
                <div className="space-y-2">
                  {result.surfaces.map((surface, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div>
                        <p className="font-medium text-slate-900">{surface.name}</p>
                        <p className="text-sm text-slate-600">{surface.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{surface.areaSqM?.toFixed(2)} m²</p>
                        <p className="text-xs text-slate-500">Confidence: {((surface.confidence || 0) * 100).toFixed(0)}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Previous Jobs */}
        {jobs.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Previous Analyses</h2>
            <div className="space-y-2">
              {jobs.slice(0, 5).map((job) => (
                <div key={job.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">{job.metadata?.objectType || 'Unknown'}</p>
                    <p className="text-sm text-slate-600">{new Date(job.createdAt).toLocaleString()}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${job.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{job.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {showLogEvidence && totals && (
        <LogQuantityEvidence projectId={projectId} sourceType="MEASUREMENT_3D" sourceId={currentJob?.id}
          observationType="MEASUREMENT" measurementType={objectType} unit="M2"
          aiQuantity={totals.netSurfaceSqM != null ? Number(totals.netSurfaceSqM.toFixed(2)) : null}
          constructionContext={constructionCtx} onClose={() => setShowLogEvidence(false)} onLogged={() => {}} />
      )}
      {showLinkActivity && currentJob?.id && (
        <LinkActivityModal projectId={projectId} evidenceType="job" evidenceId={currentJob.id}
          constructionContext={constructionCtx} onClose={() => setShowLinkActivity(false)} onLinked={() => {}} />
      )}
    </Layout>
  );
};

export default Measure3D;
